import { useMemo } from 'react'
import { Plus } from 'lucide-react'
import type { Competitor, CompetitorBrief, BriefScope } from '@ispot/shared'
import { CompetitorCategorySpread } from './CompetitorCategorySpread'

interface CompetitorAnalysisViewProps {
  competitors: Competitor[]
  briefScope: BriefScope | null
  activeBrief: CompetitorBrief | null
  isBriefLoading: boolean
  onOpenBrief: (scope: BriefScope) => void
  onCloseBrief: () => void
  onGenerateBrief: (scope: BriefScope, apiKey: string) => Promise<void>
  onEnrich?: (competitorId: string, apiKey: string) => Promise<void>
  onAddClick: () => void
}

const UNCATEGORIZED = 'Uncategorized'

function groupByCategory(competitors: Competitor[]): Array<[string, Competitor[]]> {
  const map = new Map<string, Competitor[]>()
  for (const competitor of competitors) {
    const cat = competitor.category?.trim() || UNCATEGORIZED
    if (!map.has(cat)) map.set(cat, [])
    map.get(cat)!.push(competitor)
  }
  return Array.from(map.entries()).sort(([a, aList], [b, bList]) => {
    if (a === UNCATEGORIZED) return 1
    if (b === UNCATEGORIZED) return -1
    if (bList.length !== aList.length) return bList.length - aList.length
    return a.localeCompare(b)
  })
}

export function CompetitorAnalysisView({
  competitors,
  briefScope,
  activeBrief,
  isBriefLoading,
  onOpenBrief,
  onCloseBrief,
  onGenerateBrief,
  onEnrich,
  onAddClick,
}: CompetitorAnalysisViewProps) {
  const grouped = useMemo(() => groupByCategory(competitors), [competitors])
  const expandedCompetitorId = briefScope?.type === 'competitor' ? briefScope.competitorId : null

  const handleOpenCompetitor = (id: string) => onOpenBrief({ type: 'competitor', competitorId: id })

  if (grouped.length === 0) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '14px',
          padding: '32px',
          color: 'var(--color-text-muted)',
          textAlign: 'center',
        }}
      >
        <p
          style={{
            margin: 0,
            fontFamily: 'var(--font-display)',
            fontSize: '16px',
            color: 'var(--color-text-secondary)',
            letterSpacing: '-0.01em',
          }}
        >
          Nothing to analyze yet.
        </p>
        <p style={{ margin: 0, fontSize: '13px', maxWidth: '44ch' }}>
          Add competitors or run <em>Discover</em>, then come back here to read through them as a brief.
        </p>
        <button
          type="button"
          onClick={onAddClick}
          style={{
            marginTop: '6px',
            padding: '8px 16px',
            background: 'var(--color-accent)',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Plus size={14} />
          Add competitor
        </button>
      </div>
    )
  }

  return (
    <section
      aria-label="Competitor analysis"
      style={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        padding: '28px 48px 80px',
      }}
    >
      <header style={{ maxWidth: '68ch', marginBottom: '36px' }}>
        <p
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '11px',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'var(--color-text-muted)',
            margin: 0,
          }}
        >
          Competitor analysis
        </p>
        <h2
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: '30px',
            letterSpacing: '-0.022em',
            lineHeight: 1.1,
            fontWeight: 500,
            color: 'var(--color-text)',
            margin: '10px 0 6px',
          }}
        >
          Reading the competitive picture.
        </h2>
        <p
          style={{
            margin: 0,
            color: 'var(--color-text-secondary)',
            fontSize: '15px',
            lineHeight: 1.55,
            maxWidth: '58ch',
          }}
        >
          Each category groups competitors by the job they serve. Open a brief to see strengths, weaknesses, and what they mean for Skerpa — grounded in your uploaded research.
        </p>
      </header>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '64px' }}>
        {grouped.map(([category, list]) => (
          <CompetitorCategorySpread
            key={category}
            category={category}
            competitors={list}
            expandedCompetitorId={expandedCompetitorId}
            activeBrief={activeBrief}
            isBriefLoading={isBriefLoading}
            onOpenCompetitor={handleOpenCompetitor}
            onCloseBrief={onCloseBrief}
            onGenerateBrief={onGenerateBrief}
            onEnrich={onEnrich}
          />
        ))}
      </div>
    </section>
  )
}
